import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AppLayout from '../../components/AppLayout';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  IconButton,
  Paper,
  InputAdornment,
  Stack,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import API_BASE_URL from '../../config/api';

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

export default function AddExpense() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    Month: '',
    Year: new Date().getFullYear(),
    Rent: '',
    Electricity: '',
    Snacks: '',
  });

  const [otherExpenses, setOtherExpenses] = useState([{ name: '', amount: '' }]);
  const [loading, setLoading] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleOtherExpenseChange = (index, field, value) => {
    const updated = [...otherExpenses];
    updated[index][field] = value;
    setOtherExpenses(updated);
  };

  const addOtherExpense = () => {
    setOtherExpenses(prev => [...prev, { name: '', amount: '' }]);
  };

  const removeOtherExpense = (index) => {
    if (otherExpenses.length > 1) {
      setOtherExpenses(prev => prev.filter((_, i) => i !== index));
    }
  };

  const calculateTotal = () => {
    const rent = parseFloat(formData.Rent) || 0;
    const electricity = parseFloat(formData.Electricity) || 0;
    const snacks = parseFloat(formData.Snacks) || 0;
    const otherTotal = otherExpenses.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    return rent + electricity + snacks + otherTotal;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.Month || !formData.Year) {
      toast.error('Please select month and year');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const validOtherExpenses = otherExpenses.filter(
        item => item.name.trim() && item.amount && parseFloat(item.amount) > 0
      );

      const payload = {
        ...formData,
        Month: parseInt(formData.Month),
        Year: parseInt(formData.Year),
        Rent: parseFloat(formData.Rent) || 0,
        Electricity: parseFloat(formData.Electricity) || 0,
        Snacks: parseFloat(formData.Snacks) || 0,
        OtherExpenses: validOtherExpenses,
      };

      const response = await fetch(`${API_BASE_URL}/api/expenses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        toast.success('Expense added successfully');
        navigate('/expenses');
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Failed to add expense');
      }
    } catch (error) {
      console.error('Error adding expense:', error);
      toast.error('Error adding expense');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => navigate('/expenses');

  return (
    <AppLayout>
      <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
          <IconButton
            onClick={handleBack}
            sx={{ color: '#8B7355', bgcolor: 'transparent', '&:hover': { backgroundColor: 'rgba(139,115,85,0.06)' } }}
            aria-label="back"
            size="large"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" sx={{ fontWeight: 500, letterSpacing: 2, color: '#8B7355' }}>
            ADD EXPENSE
          </Typography>
        </Box>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Left / Form column */}
            <Grid item xs={12} xl={8} sx={{ width: { xs: '100%', xl: 'auto' } }}>
              <Stack spacing={3}>
                {/* Period */}
                <Card elevation={3} sx={{ borderRadius: 2, bgcolor: '#232323' }}>
                  <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
                    <Typography variant="h6" sx={{ mb: 2, color: '#C4A574', fontWeight: 600 }}>
                      Expense Period
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          select
                          fullWidth
                          label="Month"
                          value={formData.Month}
                          onChange={(e) => handleInputChange('Month', e.target.value)}
                          required
                          size="small"
                          sx={{
                            '& .MuiOutlinedInput-root': { backgroundColor: '#2e2e2e', color: '#C4A574' },
                            '& .MuiInputLabel-root': { color: '#C4A574' },
                          }}
                        >
                          {MONTHS.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                        </TextField>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Year"
                          type="number"
                          value={formData.Year}
                          onChange={(e) => handleInputChange('Year', e.target.value)}
                          required
                          size="small"
                          inputProps={{ min: 2020, max: 2100 }}
                          sx={{
                            '& .MuiOutlinedInput-root': { backgroundColor: '#2e2e2e', color: '#C4A574' },
                            '& .MuiInputLabel-root': { color: '#C4A574' },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Fixed Expenses */}
                <Card elevation={3} sx={{ borderRadius: 2, bgcolor: '#232323' }}>
                  <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
                    <Typography variant="h6" sx={{ mb: 2, color: '#C4A574', fontWeight: 600 }}>
                      Fixed Monthly Expenses
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          fullWidth
                          label="Rent"
                          type="number"
                          value={formData.Rent}
                          onChange={(e) => handleInputChange('Rent', e.target.value)}
                          size="small"
                          InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                          sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#2e2e2e', color: '#C4A574' } }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          fullWidth
                          label="Electricity"
                          type="number"
                          value={formData.Electricity}
                          onChange={(e) => handleInputChange('Electricity', e.target.value)}
                          size="small"
                          InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                          sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#2e2e2e', color: '#C4A574' } }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={12} md={4}>
                        <TextField
                          fullWidth
                          label="Snacks"
                          type="number"
                          value={formData.Snacks}
                          onChange={(e) => handleInputChange('Snacks', e.target.value)}
                          size="small"
                          InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                          sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#2e2e2e', color: '#C4A574' } }}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Other Expenses */}
                <Card elevation={2} sx={{ borderRadius: 2 }}>
                  <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" sx={{ color: '#8B7355', fontWeight: 600 }}>
                        Other Expenses
                      </Typography>
                      <Button
                        startIcon={<AddIcon />}
                        onClick={addOtherExpense}
                        variant="outlined"
                        size="small"
                        sx={{ color: '#8B7355', borderColor: '#C4A574' }}
                      >
                        Add
                      </Button>
                    </Box>

                    <Stack spacing={2}>
                      {otherExpenses.map((expense, index) => (
                        <Paper key={index} variant="outlined" sx={{ p: 1.25 }}>
                          <Grid container spacing={1} alignItems="center">
                            <Grid item xs={12} sm={7}>
                              <TextField
                                fullWidth
                                label="Expense Name"
                                value={expense.name}
                                onChange={(e) => handleOtherExpenseChange(index, 'name', e.target.value)}
                                size="small"
                                placeholder="e.g., TV, Furniture"
                              />
                            </Grid>

                            <Grid item xs={12} sm={otherExpenses.length > 1 ? 9 : 12}>
                              <TextField
                                fullWidth
                                label="Amount"
                                type="number"
                                value={expense.amount}
                                onChange={(e) => handleOtherExpenseChange(index, 'amount', e.target.value)}
                                size="small"
                                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                              />
                            </Grid>

                            {otherExpenses.length > 1 && (
                              <Grid item xs={12} sm={3}>
                                <Button
                                  onClick={() => removeOtherExpense(index)}
                                  variant="outlined"
                                  color="error"
                                  size="small"
                                  fullWidth
                                  sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}
                                >
                                  Remove this expense
                                </Button>
                              </Grid>
                            )}
                          </Grid>
                        </Paper>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              </Stack>
            </Grid>

            {/* Right / Summary column */}
            <Grid item xs={12} xl={4} sx={{ width: { xs: '100%', xl: 'auto' } }}>
              <Card
                elevation={3}
                xs={12} xl={4}
                sx={{
                  borderRadius: 2,
                  bgcolor: '#262626',
                  position: { md: 'sticky', xs: 'static' },
                  top: { md: 20 },
                }}
              >
                <CardContent xs={12} xl={4} sx={{ p: { xs: 2, md: 3 } }}>
                  <Typography variant="h6" sx={{ mb: 2, color: '#C4A574', fontWeight: 600 }}>
                    Expense Summary
                  </Typography>

                  <Stack spacing={1.25} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Rent</Typography>
                      <Typography variant="body2">₹{(parseFloat(formData.Rent) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</Typography>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Electricity</Typography>
                      <Typography variant="body2">₹{(parseFloat(formData.Electricity) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</Typography>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Snacks</Typography>
                      <Typography variant="body2">₹{(parseFloat(formData.Snacks) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</Typography>
                    </Box>

                    <Divider sx={{ bgcolor: 'rgba(196,165,116,0.08)', my: 1 }} />

                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Other Expenses</Typography>
                      <Typography variant="body2">₹{otherExpenses.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</Typography>
                    </Box>
                  </Stack>

                  <Paper sx={{ p: 2, backgroundColor: 'rgba(196,165,116,0.12)', borderRadius: 1, mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="subtitle1" sx={{ color: '#C4A574', fontWeight: 700 }}>Total</Typography>
                      <Typography variant="h6" sx={{ color: '#C59A58', fontWeight: 700 }}>
                        ₹{calculateTotal().toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </Typography>
                    </Box>
                  </Paper>

                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Button
                        variant="outlined"
                        onClick={handleBack}
                        fullWidth
                        sx={{ color: '#8B7355', borderColor: '#C4A574' }}
                      >
                        Cancel
                      </Button>
                    </Grid>

                    <Grid item xs={6}>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={loading}
                        fullWidth
                        sx={{ bgcolor: '#C4A574', color: 'white', fontWeight: 600 }}
                      >
                        {loading ? 'Saving...' : 'Save'}
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </form>
      </Box>
    </AppLayout>
  );
}